import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, Clock3, MapPin, RefreshCw, UserCheck, UserX, Info } from "lucide-react";

import { extractErrorMessage } from "../../../../../api";
import {
    completeLearnerEventIfEligibleApi,
    declareLearnerEventAbsenceApi,
    fetchLearnerEventApi,
    registerLearnerEventApi,
} from "../../../../services/moduleEventApi";
import "./EventContentCard.css";

const statusLabels = {
    NOT_REGISTERED: "Non inscrit",
    REGISTERED: "Inscrit",
    PRESENT: "Présence confirmée",
    ABSENT: "Absence déclarée",
    COMPLETED: "Événement terminé",
};

const formatDateTime = (value) => {
    if (!value) return "Non planifié";
    try {
        return new Intl.DateTimeFormat("fr-FR", {
            dateStyle: "medium",
            timeStyle: "short",
        }).format(new Date(value));
    } catch {
        return value;
    }
};

export default function EventContentCard({ moduleId, content, onRefreshPlayer }) {
    const [eventData, setEventData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [absenceReason, setAbsenceReason] = useState("");
    const [absenceComment, setAbsenceComment] = useState("");
    const [presenceModalOpen, setPresenceModalOpen] = useState(false);
    const [presenceError, setPresenceError] = useState(null);

    const currentModuleId = moduleId || content.moduleId;
    const learnerStatus = eventData?.learnerStatus || "NOT_REGISTERED";
    const attendanceStatus = eventData?.attendance?.status || "NOT_SET";
    const isCompleted = learnerStatus === "COMPLETED" || eventData?.progress?.status === "COMPLETED";
    const eventEnded = eventData?.eventEnded || (!eventData?.futureEvent && eventData?.dateFin && new Date(eventData.dateFin) <= new Date());
    const isAlreadyRegistered =
        learnerStatus === "REGISTERED" ||
        learnerStatus === "PRESENT" ||
        learnerStatus === "COMPLETED" ||
        learnerStatus === "ABSENT" ||
        Boolean(eventData?.attendance?.registered);
    const canRegister = Boolean(eventData?.registrationOpen) && !isAlreadyRegistered;
    const canDeclareAbsence =
        Boolean(eventData?.attendance?.registered) &&
        !eventEnded &&
        attendanceStatus !== "ABSENT" &&
        attendanceStatus !== "PRESENT" &&
        learnerStatus !== "PRESENT" &&
        learnerStatus !== "ABSENT" &&
        !isCompleted;
    const canMarkPresence =
        Boolean(eventData?.attendance?.registered) &&
        eventEnded &&
        learnerStatus !== "PRESENT" &&
        learnerStatus !== "ABSENT" &&
        !isCompleted;

    const loadEvent = useCallback(async () => {
        if (!currentModuleId || !content?.id) return;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchLearnerEventApi(currentModuleId, content.id);
            setEventData(response.data);
        } catch (err) {
            setError(extractErrorMessage(err, "Impossible de charger l'événement."));
        } finally {
            setLoading(false);
        }
    }, [content?.id, currentModuleId]);

    useEffect(() => {
        loadEvent();
    }, [loadEvent]);

    useEffect(() => {
        if (!canMarkPresence) {
            setPresenceModalOpen(false);
            setPresenceError(null);
        }
    }, [canMarkPresence]);

    const runAction = async (action) => {
        setActionLoading(true);
        setError(null);
        try {
            await action();
            const response = await fetchLearnerEventApi(currentModuleId, content.id);
            setEventData(response.data);
            onRefreshPlayer?.();
            return true;
        } catch (err) {
            setError(extractErrorMessage(err, "Action événement impossible."));
            return false;
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegister = () => runAction(() => registerLearnerEventApi(currentModuleId, content.id));
    const openPresenceModal = () => {
        setPresenceError(null);
        setPresenceModalOpen(true);
    };
    const closePresenceModal = () => {
        if (actionLoading) return;
        setPresenceError(null);
        setPresenceModalOpen(false);
    };
    const handleConfirmPresence = async () => {
        setActionLoading(true);
        setPresenceError(null);
        setError(null);
        try {
            await completeLearnerEventIfEligibleApi(currentModuleId, content.id);
            const response = await fetchLearnerEventApi(currentModuleId, content.id);
            setEventData(response.data);
            setPresenceModalOpen(false);
            onRefreshPlayer?.();
        } catch (err) {
            setPresenceError(extractErrorMessage(err, "Impossible de confirmer votre présence pour le moment."));
        } finally {
            setActionLoading(false);
        }
    };
    const handleAbsence = async () => {
        if (!absenceReason.trim()) {
            setError("Le motif d'absence est obligatoire.");
            return;
        }
        const success = await runAction(() =>
            declareLearnerEventAbsenceApi(currentModuleId, content.id, {
                absenceReason: absenceReason.trim(),
                comment: absenceComment.trim() || null,
            })
        );
        if (success) {
            setAbsenceReason("");
            setAbsenceComment("");
        }
    };

    const statusBadgeClass = useMemo(() => {
        if (isCompleted) return "ecc-badge ecc-badge--success";
        if (learnerStatus === "ABSENT") return "ecc-badge ecc-badge--error";
        if (learnerStatus === "REGISTERED" || learnerStatus === "PRESENT") return "ecc-badge ecc-badge--info";
        return "ecc-badge";
    }, [isCompleted, learnerStatus]);

    if (loading) {
        return (
            <div className="ecc-shell" style={{ alignItems: "center", padding: "40px" }}>
                <RefreshCw className="ecc-spin" size={20} />
                <span style={{ fontSize: 13, color: "var(--neutral-600)", marginTop: 8 }}>Chargement de l'événement…</span>
            </div>
        );
    }

    if (!eventData) {
        return (
            <div className="ecc-shell ecc-shell--error">
                <Info size={32} />
                <strong>Événement indisponible</strong>
                <p>{error || "Aucune donnée événement n'a été retournée."}</p>
            </div>
        );
    }

    return (
        <div className="ecc-shell">
            {/* ── Heading ── */}
            <div className="ecc-heading">
                <div className="ecc-title-group">
                    <span className={statusBadgeClass}>{statusLabels[learnerStatus] || learnerStatus}</span>
                    <h3>{eventData.title || content.title}</h3>
                    {eventData.description ? <p>{eventData.description}</p> : null}
                </div>
                <button className="ecc-refresh-btn" type="button" onClick={loadEvent} disabled={actionLoading} title="Actualiser">
                    <RefreshCw size={15} />
                </button>
            </div>

            {/* ── Stat grid ── */}
            <div className="cr-stat-grid">
                <div className="cr-stat-card">
                    <span><CalendarDays size={13} /> Début</span>
                    <strong>{formatDateTime(eventData.dateDebut)}</strong>
                </div>
                <div className="cr-stat-card">
                    <span><Clock3 size={13} /> Fin</span>
                    <strong>{formatDateTime(eventData.dateFin)}</strong>
                </div>
                <div className="cr-stat-card">
                    <span><MapPin size={13} /> Lieu</span>
                    <strong>{eventData.locationLabel || "Non renseigné"}</strong>
                </div>
                <div className="cr-stat-card">
                    <span><UserCheck size={13} /> Statut</span>
                    <strong>{statusLabels[learnerStatus] || learnerStatus}</strong>
                </div>
            </div>

            {/* ── Notices ── */}
            {eventData.futureEvent && (
                <div className="ecc-notice">
                    <Info size={15} />
                    <span>Cet événement est à venir. La progression sera validée après confirmation de votre présence.</span>
                </div>
            )}

            {isCompleted && (
                <div className="ecc-notice ecc-notice--success">
                    <CheckCircle2 size={15} />
                    <span>Présence confirmée — contenu événement terminé.</span>
                </div>
            )}

            {/* ── Error ── */}
            {error && <div className="ecc-error-msg">{error}</div>}

            {/* ── Actions — bouton S'inscrire masqué si déjà inscrit ── */}
            <div className="ecc-actions">
                {canRegister && (
                    <button className="cr-btn cr-btn--primary" type="button" onClick={handleRegister} disabled={actionLoading}>
                        <UserCheck size={15} />
                        S'inscrire à l'événement
                    </button>
                )}
                {canMarkPresence && (
                    <button
                        className="cr-btn cr-btn--secondary"
                        type="button"
                        onClick={openPresenceModal}
                        disabled={actionLoading}
                    >
                        <CheckCircle2 size={15} />
                        Marquer ma présence
                    </button>
                )}
            </div>

            {/* ── Absence section ── */}
            {canDeclareAbsence && (
                <div className="ecc-absence-section">
                    <h4>Déclarer une absence</h4>
                    <div className="ecc-form-group">
                        <label>Motif d'absence *</label>
                        <input
                            className="cr-input"
                            value={absenceReason}
                            onChange={(e) => setAbsenceReason(e.target.value)}
                            placeholder="Ex : raison médicale…"
                            maxLength={255}
                        />
                    </div>
                    <div className="ecc-form-group">
                        <label>Commentaire <span style={{ fontWeight: 300, textTransform: "none", letterSpacing: 0 }}>(optionnel)</span></label>
                        <textarea
                            className="cr-textarea"
                            value={absenceComment}
                            onChange={(e) => setAbsenceComment(e.target.value)}
                            placeholder="Précisez si besoin…"
                            maxLength={2000}
                            rows={3}
                        />
                    </div>
                    <button className="cr-btn cr-btn--danger" type="button" onClick={handleAbsence} disabled={actionLoading}>
                        <UserX size={15} />
                        Déclarer mon absence
                    </button>
                </div>
            )}

            {presenceModalOpen && (
                <div className="ecc-modal-overlay" onClick={closePresenceModal}>
                    <div className="ecc-modal" onClick={(evt) => evt.stopPropagation()}>
                        <div className="ecc-modal-header">
                            <h4>Confirmer ma présence</h4>
                        </div>
                        <div className="ecc-modal-body">
                            <strong>{eventData.title || content.title}</strong>
                            <span>{formatDateTime(eventData.dateDebut)} - {formatDateTime(eventData.dateFin)}</span>
                            {presenceError && <div className="ecc-error-msg">{presenceError}</div>}
                        </div>
                        <div className="ecc-modal-footer">
                            <button className="cr-btn cr-btn--secondary" type="button" onClick={closePresenceModal} disabled={actionLoading}>
                                Annuler
                            </button>
                            <button className="cr-btn cr-btn--primary" type="button" onClick={handleConfirmPresence} disabled={actionLoading}>
                                <CheckCircle2 size={15} />
                                Confirmer ma présence
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
